import { AionCta } from "@/components/site/AionCta";
import { KeywordHubLinks } from "@/components/site/KeywordHubLinks";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Building2, Sparkles } from "lucide-react";

export const Route = createFileRoute("/ventures")({
  head: () => ({
    meta: [
      { title: "Raj Shamani Business — House of X & Shamani Industries", key: "title" },
      { name: "description", content: "Raj Shamani's business ventures and investments — House of X, Shamani Industries and 20+ startup bets across D2C, fintech and the creator economy.", key: "description" },
      { property: "og:title", content: "Raj Shamani Business — House of X & Shamani Industries", key: "og:title" },
      { property: "og:description", content: "Raj Shamani's business ventures and investments — House of X, Shamani Industries and 20+ startup bets across D2C, fintech and the creator economy.", key: "og:description" },
      { property: "og:url", content: "https://rajshamani.life/ventures", key: "og:url" },
      { property: "og:type", content: "website", key: "og:type" },
      { property: "og:site_name", content: "Raj Shamani", key: "og:site_name" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { property: "og:image", content: "https://rajshamani.life/__l5e/assets-v1/f96c7871-158d-4f3b-b229-03c533af2f1c/og-ventures.jpg", key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani — ventures, investments and portfolio", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "twitter:title", content: "Raj Shamani Business — House of X & Shamani Industries", key: "twitter:title" },
      { name: "twitter:description", content: "Raj Shamani's business ventures and investments — House of X, Shamani Industries and 20+ startup bets across D2C, fintech and the creator economy.", key: "twitter:description" },
      { name: "twitter:image", content: "https://rajshamani.life/__l5e/assets-v1/f96c7871-158d-4f3b-b229-03c533af2f1c/og-ventures.jpg", key: "twitter:image" },
    ],
    links: [{ rel: "canonical", href: "https://rajshamani.life/ventures" }],
    scripts: [{
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Person",
        "name": "Raj Shamani",
        "url": "https://rajshamani.life/ventures",
        "jobTitle": "Founder & Angel Investor",
        "description": "Entrepreneur and early-stage investor in consumer, fintech, and creator economy startups across India.",
        "sameAs": [
          "https://www.youtube.com/@rajshamani",
          "https://www.instagram.com/rajshamani",
          "https://www.linkedin.com/in/rajshamani",
          "https://twitter.com/rajshamani",
        ],
        "worksFor": [
          { "@type": "Organization", "name": "Shamani Industries" },
          { "@type": "Organization", "name": "Figuring Out Media" },
          { "@type": "Organization", "name": "House of X" },
        ],
        "knowsAbout": ["Entrepreneurship", "Startups", "Venture Capital", "Consumer Brands", "Fintech", "Creator Economy"],
      }),
    }, {
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Raj Shamani Business — House of X & Shamani Industries",
        headline: "Raj Shamani Business — House of X & Shamani Industries",
        description: "Raj Shamani's business ventures and investments — House of X, Shamani Industries and 20+ startup bets across D2C, fintech and the creator economy.",
        url: "https://rajshamani.life/ventures",
        inLanguage: "en",
        isPartOf: { "@type": "WebSite", name: "Raj Shamani", url: "https://rajshamani.life/" },
        about: { "@type": "Person", name: "Raj Shamani" },
        primaryImageOfPage: {
          "@type": "ImageObject",
          url: "https://rajshamani.life/__l5e/assets-v1/f96c7871-158d-4f3b-b229-03c533af2f1c/og-ventures.jpg",
        },
        image: "https://rajshamani.life/__l5e/assets-v1/f96c7871-158d-4f3b-b229-03c533af2f1c/og-ventures.jpg",
      }),
    }],
  }),
  component: Ventures,
});

const founded = [
  { n: "Shamani Industries", c: "Manufacturing", d: "India's leading manufacturer of polymer additives, founded by family — now scaling globally." },
  { n: "Figuring Out Media", c: "Media", d: "Multi-format content studio behind the Figuring Out podcast and original series." },
  { n: "House of X", c: "Consumer Brands", d: "Building a portfolio of next-gen Indian D2C brands." },
];

const invested = [
  { n: "GIVA", s: "Jewelry" },
  { n: "Snitch", s: "Fashion" },
  { n: "Bummer", s: "D2C" },
  { n: "BeerBiceps Skill House", s: "Creator Economy" },
];

function Ventures() {
  return (
    <div>
      <PageBreadcrumb current="Ventures" />
      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4">Ventures</div>
          <h1 className="text-5xl md:text-6xl font-bold leading-tight">
            Raj Shamani Business — Ventures &amp; <span className="text-gradient-gold">Investments</span>
          </h1>
          <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
            I build companies I'd want to use. I back founders I'd want to
            work for. Here's the current portfolio.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24">
        <div className="flex items-center gap-3 mb-10">
          <Building2 className="text-gold" />
          <h2 className="text-2xl md:text-3xl font-bold">Founded & Co-Founded</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {founded.map((f) => (
            <div key={f.n} className="p-8 rounded-xl bg-card border border-border hover:border-gold/40 transition">
              <div className="text-xs uppercase tracking-widest text-gold mb-3">{f.c}</div>
              <h3 className="text-xl font-bold mb-3">{f.n}</h3>
              <p className="text-sm text-muted-foreground">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 pb-24">
        <div className="flex items-center gap-3 mb-10">
          <Sparkles className="text-gold" />
          <h2 className="text-2xl md:text-3xl font-bold">Selected Investments</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {invested.map((i) => (
            <div key={i.n} className="p-6 rounded-xl bg-card border border-border hover:border-gold/40 transition">
              <div className="font-bold mb-1">{i.n}</div>
              <div className="text-xs text-muted-foreground uppercase tracking-wider">{i.s}</div>
            </div>
          ))}
        </div>
        <p className="mt-8 text-sm text-muted-foreground max-w-2xl">
          Plus 15+ additional undisclosed positions across fintech, healthtech,
          edtech and coworking — held privately at the request of founders and co-investors.
          Read more about the family manufacturing business at{" "}
          <Link to="/shamani-industries" className="text-gold underline underline-offset-4">
            Shamani Industries
          </Link>
          .
        </p>
      </section>

      <AionCta />
      <KeywordHubLinks className="pb-8" />

    </div>
  );
}
